import { cloneDeep, each, escapeRegExp } from "lodash";
import { getReCaptchaWidgetId } from "./inputs";

declare const grecaptcha: any;
declare const jQuery: any;
const $ = jQuery;

const ajaxCall: Function = window["iw"].utils.ajaxCall;
const getLocalStorage: Function = window["iw"].utils.getLocalStorage;
const setLocalStorage: Function = window["iw"].utils.setLocalStorage;

export type PostType = "product"|"supplier"|"expert"|"event";

export interface EnquiryConfig
{
    postType: PostType;
    postId?: number;
    cat?: number;
    subcat?: number;
    initial?: string;
    country?: number;
    keyword?: string;
    offset?: number;
    supplierIds?: number[]; // for expert enquiries
}

export interface EnquiryResult
{
    ok: boolean;
    userErrorMsg?: string;
}

const stopProp = (event) => {
    event.stopPropagation();
};

const localStoreKey = "enquiry_form";

const storeInputValue = (name: string, value: string): void => {
    const data: any = getLocalStorage(localStoreKey) || {};
    data[name] = value || "";
    setLocalStorage(localStoreKey, data);
};

const populateInputValues = ($textInputs): void => {
    const data: any = getLocalStorage(localStoreKey);
    if (!data)
    {
        return;
    }
    each(data, (value: string, name: string) => {
        $textInputs.filter(`[name="${name}"]`).val(value);
    });
};

function setupTextInputStorage($textInputs): void
{
    $textInputs = $textInputs.not(`[name="enquiry"]`);
    $textInputs.on("change", function () {
        const $input = $(this);
        storeInputValue($input.attr("name"), $input.val());
    });
    populateInputValues($textInputs);
}

export function initEnquiryForm($wrap, config: EnquiryConfig, defaultEnquiryText: string = "", catNameForTemplateText: string = ""): void
{
    const $submit = $wrap.find(`[data-submit]`).removeAttr("disabled").removeClass("busy-button").css({ display: "" });
    const $successMsg = $wrap.find(`.submission-success-message`).removeClass("show");
    const $errorMsg = $wrap.find(`.submission-failed-message`).removeClass("show");
    const $inputWraps = $wrap.find(`.popup-input`).removeClass("error");
    const $textInputs = $wrap.find(`.popup-input-text :input`);
    $wrap.find(`.popup-input [name="enquiry"]`).val(defaultEnquiryText || "");

    $(`[data-batch-only]`).css({ display: config.hasOwnProperty("postId") ? "none" : "" });

    populateEnquiryFormTemplateText($wrap, catNameForTemplateText, config);

    setupTextInputStorage($textInputs);

    const $captchaWrap = $wrap.find(`.popup-input-recaptcha`);

    if (($captchaWrap.length > 0) && ("grecaptcha" in window)) {
        const captchaId = getReCaptchaWidgetId($captchaWrap);
        grecaptcha.reset(captchaId);
    } else {
        console.warn(`captcha missing on page load`);
    }

    let busy = false;

    $wrap.off("submit success error complete", stopProp).on("submit success error complete", stopProp);

    const gatherValidatedData = () => {
        $inputWraps.removeClass("error");
        const data: any = {};
        let error = false;
        const doCaptcha: boolean = ($captchaWrap.length > 0) && ("grecaptcha" in window);
        if (doCaptcha) {
            const captchaId = getReCaptchaWidgetId($captchaWrap);
            data.captcha = grecaptcha.getResponse(captchaId);
            if (!data.captcha) {
                alert(`Please complete the ReCaptcha`);
                return undefined;
            }
        } else {
            console.warn(`captcha missing on page load`);
        }
        $textInputs.each(function () {
            const $inp = $(this);
            const required: boolean = $inp.is('[required]');
            const val = $.trim($inp.val());
            if (required && !val)
            {
                $inp.closest(`.popup-input`).addClass("error");
                error = true;
                return;
            }
            if (val && ($inp.attr("type") === "email") && !val.match(/.\@.+\../))
            {
                $inp.closest(`.popup-input`).addClass("error");
                error = true;
                return;
            }
            const name: string = $inp.attr("name");
            data[name] = val;
        });
        if (error)
        {
            alert(`Please make sure you have filled in all inputs correctly.`);
            return undefined;
        }
        data.sendToAll = $wrap.find(`input[name="sendToAll"]`).prop("checked");
        return data;
    };

    const submitEnquiry = async function (): Promise<void> {
        if (busy)
        {
            return;
        }
        const ajaxData: any = gatherValidatedData();
        $successMsg.removeClass("show");
        $errorMsg.removeClass("show");
        if (!ajaxData)
        {
            // validation error
            return;
        }
        busy = true;
        ajaxData.config = cloneDeep(config);
        delete ajaxData.config.offset;
        delete ajaxData.config.length; // in case we ever decide to set 'length' in this module
        if (ajaxData.sendToAll)
        {
            // Remove filters
            delete ajaxData.config.subcat;
            delete ajaxData.config.initial;
            delete ajaxData.config.country;
            delete ajaxData.config.keyword;
        }
        let result: EnquiryResult;
        $submit.attr("disabled", "disabled").addClass("busy-button");

        $wrap.trigger("submit");
        
        let userErrorMsg: string = `Sorry, there was a problem and your enquiry could not be sent. Please try again later.`;
        try
        {
            result = await ajaxCall("hs_submit_enquiry", ajaxData);
            if (!result || !result.ok)
            {
                console.error(`Submit enquiry error result:`, result);
                if (result && result.userErrorMsg)
                {
                    userErrorMsg = result.userErrorMsg;
                }
                throw new Error(`No result/result.ok in enquiry result obj`);
            }
        }
        catch (error)
        {
            if (error && error.response && error.response.userErrorMsg)
            {
                userErrorMsg = error.response.userErrorMsg;
            }
            $wrap.trigger("error");
            console.error("Supplier enquiry submit failed", error);
            $errorMsg.html(userErrorMsg).addClass("show");
            $submit.removeAttr("disabled");
            return;
        }
        finally
        {
            busy = false;
            $wrap.trigger("complete");
            $submit.removeClass("busy-button");
        }

        $successMsg.addClass("show");
        $submit.css( { display: "none" }); // Hide submit button to avoid double-submissions
        $wrap.trigger("success");
    };
    
    // Submit Button Click
    $submit.off("click").on("click", function (event): false {
        event.stopPropagation();
        event.preventDefault();
        submitEnquiry();
        return false;
    });
}

function populateEnquiryFormTemplateText($wrap, catName: string, config: EnquiryConfig): void
{
    const vars = {
        postTypePlural: config.postType + "s",
        catName,
    };
    $wrap.find(`[data-template-text]`).each(function () {
        const $el = $(this);
        let str: string = $el.attr("data-template-text");
        each(vars, (value: string, name: string) => {
            if (value === undefined)
            {
                value = "";
            }
            const regex: RegExp = new RegExp(escapeRegExp(`{{${name}}}`), "gi");
            str = str.replace(regex, value);
        });
        $el.html(str);
    });
}
